import { MeiucaService as BaseMeiucaService } from '@meiuca/service'
import { env } from '@/lib/env'

/**
 * Public acceptance flow of the training center's general term
 * (`/public/v1/contract-terms/{token}`): read the rendered term, request the
 * OTP (sent to the signer's registered phone — the student, or their
 * guardian; never a phone typed on the page), verify it, and accept.
 *
 * Mirrors `publicChargeService`: unauthenticated surface, shared axios
 * configuration and the typed error hierarchy from `@meiuca/service`.
 * Interesting backend error codes callers branch on:
 * - `contract_terms_changed` (409): contract changed between load and accept —
 *   reload the term and start over.
 * - `contract_terms_already_accepted` (409): someone finished first — treat
 *   as success and reload.
 * - `contract_terms_otp_invalid` (401): wrong/expired code.
 * - `contract_terms_verification_invalid` (401): verification token expired
 *   or consumed — restart from the OTP step.
 * - `contract_terms_payer_phone_missing` (409): the signer has no phone on file.
 */
export interface PublicContractTermsPayload {
  /** `outdated`: accepted before, but the center's rules changed — sign again. */
  status: 'pending' | 'outdated' | 'accepted'
  centerName: string
  /** Full document markdown. Accepted: the stored record, never a re-render. */
  content: string
  contentSha256: string
  templateVersion: number
  signerDisplayName: string
  /** Masked phone the OTP goes to; null when the signer has no phone. */
  maskedPhone: string | null
  acceptedAt: string | null
  /** Last accepted version's date, when `outdated`. */
  previousAcceptedAt?: string | null
  /** Legacy pending per-contract link: switch to `/terms/<redirectToken>`. */
  redirectToken?: string | null
  /** Who issues the term. Missing = training center (older backend). */
  issuerKind?: 'training_center' | 'arena' | null
}

export interface ContractTermsOtpSendResult {
  expiresIn: number
  resendAfter: number
  maskedPhone: string
}

class ContractTermsService extends BaseMeiucaService {
  /** `acceptanceId` opens that stored acceptance as a receipt. */
  async getByToken(token: string, acceptanceId?: string | null): Promise<PublicContractTermsPayload> {
    const query = acceptanceId ? `?a=${encodeURIComponent(acceptanceId)}` : ''
    const response = await this.get<{ data: PublicContractTermsPayload }>(
      `/public/v1/contract-terms/${encodeURIComponent(token)}${query}`
    )
    return response.data.data
  }

  async sendOtp(token: string): Promise<ContractTermsOtpSendResult> {
    const response = await this.post<{ data: ContractTermsOtpSendResult }>(
      `/public/v1/contract-terms/${encodeURIComponent(token)}/otp/send`
    )
    return response.data.data
  }

  async verifyOtp(token: string, otpCode: string): Promise<string> {
    const response = await this.post<{ data: { verificationToken: string } }>(
      `/public/v1/contract-terms/${encodeURIComponent(token)}/otp/verify`,
      { otpCode }
    )
    return response.data.data.verificationToken
  }

  async accept(
    token: string,
    verificationToken: string,
    contentSha256: string
  ): Promise<{ acceptedAt: string }> {
    const response = await this.post<{ data: { acceptedAt: string } }>(
      `/public/v1/contract-terms/${encodeURIComponent(token)}/accept`,
      { verificationToken, contentSha256 }
    )
    return response.data.data
  }
}

export const contractTermsService = new ContractTermsService({
  // Same reasoning as publicChargeService: the public surface lives at the
  // API host root under `public/v1`, addressed by its own env var.
  baseURL: env.PUBLIC_API_URL,
  refreshEndpoint: '/v1/auth/refresh',
  cookiePrefix: '',
  debug: env.IS_DEVELOPMENT
})
